import React, { useState } from 'react';
import useDraggable from '../hooks/useDraggable';
import useTrackPageView from '../hooks/useTrackPageView';
import { useTranslation } from '../i18n/LanguageContext';

// RiskHelp.jsx/SweepHelp.jsx'teki AYNI küçük sunum bileşenleri (sh-* CSS sınıfları) — görsel
// tutarlılık için burada da aynı şekilde kullanılıyor.
const Section = ({ title, children }) => (
  <div className="sh-section">
    <h3 className="sh-section-title">{title}</h3>
    {children}
  </div>
);

const Param = ({ name, unit, recommended, children }) => (
  <div className="sh-param">
    <div className="sh-param-header">
      <span className="sh-param-name">{name}</span>
      {unit && <span className="sh-param-unit">{unit}</span>}
      {recommended && <span className="sh-param-rec">{recommended}</span>}
    </div>
    <div className="sh-param-body">{children}</div>
  </div>
);

const Example = ({ label, children }) => (
  <div className="sh-example">
    <span className="sh-example-label">{label}</span>
    <span className="sh-example-text">{children}</span>
  </div>
);

const Note = ({ children }) => (
  <div style={{ background: 'rgba(13,148,136,.08)', border: '1px solid rgba(13,148,136,.25)', borderRadius: 7, padding: '8px 12px', fontSize: 12, color: 'var(--accent)', marginTop: 8 }}>
    {children}
  </div>
);

const Warn = ({ children }) => (
  <div style={{ background: 'rgba(220,38,38,.06)', border: '1px solid rgba(220,38,38,.25)', borderRadius: 7, padding: '8px 12px', fontSize: 12, color: '#b91c1c', marginTop: 8 }}>
    {children}
  </div>
);

const Timeline = ({ steps }) => (
  <div className="sh-timeline">
    {steps.map((s, i) => (
      <div key={i} className="sh-timeline-step">
        <div className="sh-timeline-dot" style={{ background: s.color || 'var(--accent)' }} />
        <div className="sh-timeline-content">
          <div className="sh-timeline-label" style={{ color: s.color || 'var(--accent)' }}>{s.label}</div>
          <div className="sh-timeline-text">{s.text}</div>
        </div>
      </div>
    ))}
  </div>
);

// Bu dosyanın içeriği yoğun iç-içe biçimlendirme (<strong>/<em>/<code>) barındıran uzun anlatım
// metinlerinden oluşuyor — düz dize sözlüğü (i18n/tr.js) yerine, HER sekme kendi TR/EN içerik
// nesnesini burada tanımlar ve `lang`'a göre hangisinin kullanılacağını seçer. Bu, biçimlendirmeyi
// kaybetmeden (300'den fazla parçalanmış anahtar yerine) okunabilir kalmasını sağlar.

// ── 1. Başlarken: Kayıt, Giriş, Abonelik ─────────────────────────────
function GettingStartedTab({ lang }) {
  const isEn = lang === 'en';
  return (
    <>
      <Section title={isEn ? '👤 Creating an Account (Sign Up)' : '👤 Hesap Oluşturma (Üye Ol)'}>
        {isEn ? (
          <p className="sh-text">
            The login screen has two tabs: <strong>Log In</strong> and <strong>Create Account</strong>.
            To create a new account, switch to "Create Account" and enter:
          </p>
        ) : (
          <p className="sh-text">
            Giriş ekranında iki sekme vardır: <strong>Giriş Yap</strong> ve <strong>Hesap Oluştur</strong>.
            Yeni bir hesap için "Hesap Oluştur" sekmesine geçin ve şu bilgileri girin:
          </p>
        )}
        <Param name={isEn ? 'Full Name' : 'Ad Soyad'}>
          <p>{isEn ? 'Your display name — used on invoices and in your profile.' : 'Görünen adınız — faturalarda ve profil bilgilerinde kullanılır.'}</p>
        </Param>
        <Param name={isEn ? 'Email' : 'E-posta'}>
          <p>{isEn
            ? 'You can also use this address instead of your username when logging in. No verification email is sent, but it may be needed for password reset later.'
            : 'Giriş yaparken kullanıcı adı yerine bu adresi de girebilirsiniz. Doğrulama e-postası gönderilmez, ama şifre sıfırlama için ileride gerekebilir.'}</p>
        </Param>
        <Param name={isEn ? 'Password' : 'Şifre'} recommended={isEn ? 'At least 10 characters, a letter and a digit' : 'En az 10 karakter, harf ve rakam'}>
          <p>{isEn ? 'Passwords that don\'t meet this rule are rejected. Click the eye icon to reveal the password.' : 'Bu kurala uymayan şifreler kabul edilmez. Göz ikonuna basarak şifreyi görünür yapabilirsiniz.'}</p>
        </Param>
        {isEn ? (
          <p className="sh-text">
            You must check the KVKK/data-notice consent checkboxes below the form (including your
            email/SMS contact preferences). You can also use <strong>"Continue with Google"</strong> to
            sign up/log in with one click, without a password.
          </p>
        ) : (
          <p className="sh-text">
            Formun altındaki KVKK/aydınlatma onay kutucuklarını işaretlemeniz (e-posta/SMS ile iletişim
            tercihleri dahil) gerekir. Ayrıca <strong>"Google ile devam et"</strong> butonuyla, şifre
            girmeden Google hesabınızla tek tıkla kayıt olabilir/giriş yapabilirsiniz.
          </p>
        )}
        <Note>
          {isEn ? (
            <>Every new account starts <strong>without a subscription (inactive)</strong>. You can't open
            real (live) manual trades until you subscribe — but you can use <strong>Test mode</strong>
            without limits (see below).</>
          ) : (
            <>Yeni oluşturulan her hesap <strong>abonesiz (inactive)</strong> başlar. Abone olana kadar
            gerçek (canlı) manuel işlem açamazsınız — ama <strong>Test modunu</strong> sınırsız
            kullanabilirsiniz (aşağıya bakın).</>
          )}
        </Note>
      </Section>

      <Section title={isEn ? '🔑 Logging In' : '🔑 Giriş Yapma'}>
        <p className="sh-text">
          {isEn
            ? <>On the "Log In" tab, enter your email <em>or</em> username and your password, then click
              "LOG IN". "Continue with Google" is also available here.</>
            : <>"Giriş Yap" sekmesinde e-posta adresinizi <em>veya</em> kullanıcı adınızı, ve şifrenizi
              girip "GİRİŞ YAP" butonuna basmanız yeterli. "Google ile devam et" seçeneği burada da
              mevcuttur.</>}
        </p>
      </Section>

      <Section title={isEn ? '💳 Subscription' : '💳 Abonelik'}>
        <p className="sh-text">
          {isEn
            ? 'Subscriptions are activated by the platform admin on your account — there is no self-service purchase flow. Until it\'s activated you can still use everything in Test mode without limits (see below).'
            : 'Abonelikler, platform yöneticisi tarafından hesabınız üzerinde aktive edilir — uygulama içinden kendi kendine satın alma akışı yoktur. Aktive edilene kadar Test modunu sınırsız kullanabilirsiniz (aşağıya bakın).'}
        </p>
      </Section>

      <Section title={isEn ? '🧪 Demo / Test Mode — What Can I Do Without a Subscription?' : '🧪 Demo / Test Modu — Abonelik Olmadan Ne Yapabilirim?'}>
        <p className="sh-text">
          {isEn
            ? <>If you don't have an active subscription (or your trial ended), the system automatically
              treats you as being in "demo mode": <strong>you can't open trades with real money</strong>
              (Long/Short and the bot's "Start" buttons are locked). But to see the platform's LOGIC in
              action:</>
            : <>Aktif bir aboneliğiniz yoksa (ya da deneme süreniz bittiyse) sistem sizi otomatik olarak
              "demo modda" kabul eder: <strong>gerçek para ile işlem açamazsınız</strong> (Long/Short ve
              Bot "Başlat" butonları kilitlenir). Ama platformun MANTIĞINI görmek için:</>}
        </p>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--muted)', lineHeight: 1.7 }}>
          <li>{isEn ? <>The <strong>Long (Test) / Short (Test)</strong> buttons for manual trades are always available.</> : <>Manuel işlemlerde <strong>Long (Test) / Short (Test)</strong> butonları her zaman açıktır.</>}</li>
          <li>{isEn ? <>The <strong>"Start (Test)"</strong> button for starting a bot is always available.</> : <>Bot başlatmada <strong>"Başlat (Test)"</strong> butonu her zaman açıktır.</>}</li>
          <li>{isEn ? 'Backtest, Parameter Sweep and Trade Visualization are fully usable regardless of subscription.' : 'Backtest, Parametre Taraması ve İşlem Görselleştirmesi aboneliğe bakılmaksızın tamamen kullanılabilir.'}</li>
        </ul>
        <Note>
          {isEn
            ? 'Test trades never send any real order to OKX — price/PnL is calculated from real market data but simulated. You can tell them apart from other trades in Trade History (purple ⚗ mark).'
            : 'Test işlemleri OKX\'e hiçbir gerçek emir göndermez — fiyat/PnL hesaplaması gerçek piyasa verisiyle ama simüle edilerek yapılır. Sonuçları İşlem Geçmişi\'nde diğer işlemlerden ayırt edebilirsiniz (mor ⚗ işareti).'}
        </Note>
      </Section>

      <Section title={isEn ? '🔢 How Many Bots/Manual Trades Can I Run at Once?' : '🔢 Aynı Anda Kaç Bot/Manuel İşlem Açabilirim?'}>
        <p className="sh-text">
          {isEn
            ? <>Running bots and open manual trades share the <strong>SAME combined pool</strong> — across
              different pairs, at most this many can be open/running at the same time:</>
            : <>Çalışan botlar ve açık manuel işlemler <strong>AYNI ortak havuzu</strong> paylaşır — farklı
              paritelerde toplamda en fazla şu kadarı aynı anda açık/çalışır olabilir:</>}
        </p>
        <Param name={isEn ? 'Unsubscribed user' : 'Abonesiz kullanıcı'} recommended={isEn ? 'total: 1' : 'toplam 1'}>
          <p>{isEn ? <>Bot or manual trade, doesn\'t matter — only in <strong>test</strong> mode, one slot at a time.</> : <>Bot ya da manuel işlem fark etmez — sadece <strong>test</strong> modunda, aynı anda tek bir slot.</>}</p>
        </Param>
        <Param name={isEn ? 'Subscribed user' : 'Abone kullanıcı'} recommended={isEn ? 'total: 4' : 'toplam 4'}>
          <p>{isEn
            ? <>Bots and manual trades mixed freely, <strong>test/live can be mixed freely</strong> — up to
              4 different pairs can be open/running at the same time.</>
            : <>Bot ve manuel işlem karışık olarak, <strong>test/canlı serbestçe karıştırılabilir</strong> —
              toplamda 4 farklı parite aynı anda açık/çalışır olabilir.</>}</p>
        </Param>
        <Note>
          {isEn
            ? 'Once you hit this limit, any new bot/manual trade attempt is rejected — you\'ll need to stop an existing bot or close a manual trade first.'
            : 'Bu sınıra ulaştığınızda yeni bir bot/manuel işlem açma denemesi reddedilir — önce mevcut bir bot\'u durdurmanız ya da bir manuel işlemi kapatmanız gerekir.'}
        </Note>
      </Section>
    </>
  );
}

// ── 2. Ana Ekran Turu ─────────────────────────────────────────────────
function DashboardTourTab({ lang }) {
  const isEn = lang === 'en';
  return (
    <>
      <Section title={isEn ? '🗺 Overall Layout' : '🗺 Genel Yerleşim'}>
        <p className="sh-text">{isEn ? 'The main screen has four regions:' : 'Ana ekran dört ana bölgeden oluşur:'}</p>
        <Param name={isEn ? 'Left Panel — Bot Settings' : 'Sol Panel — Bot Ayarları'}>
          <p>{isEn
            ? <>Market (Spot/Futures) selection, pair search, Margin+Leverage on Futures or Amount (USDT)
              on Spot. This is where you put a pair into "trace" mode. Below it, if present, the
              <strong> "Active Bots"</strong> (running bots + Stop button) and
              <strong> "Open Manual Trades"</strong> (pair/side/SL/TP info + Close button) cards are listed.</>
            : <>Piyasa (Spot/Vadeli) seçimi, parite arama, Vadeli'de Marjin+Kaldıraç ya da Spot'ta
              Miktar (USDT) girişi. Buradan bir pariteyi "izlemeye" (trace) alırsınız. Altında,
              varsa, <strong>"Aktif Botlar"</strong> (çalışan botlar + Durdur butonu) ve
              <strong> "Açık Manuel İşlemler"</strong> (parite/yön/SL/TP bilgisi + Kapat butonu)
              kartları listelenir.</>}</p>
        </Param>
        <Param name={isEn ? 'Center — Chart' : 'Orta — Grafik'}>
          <p>{isEn
            ? <>Candle chart for the selected pair: MA50/MA200/RSI/SMA are always drawn; if the
              strategy is Range Bar+HMA+ADX, optional HMA/ADX lines can also be
              added (checkboxes under Settings &gt; Signal). At the top there's a period selector and a
              "↺ Reset" button; at the bottom, a live-updating OHLC + indicator strip as you hover
              (including candle stats like O/C%, High/Low%).</>
            : <>Seçili paritenin mum grafiği: MA50/MA200/RSI/SMA her zaman çizilir; strateji
              Range Bar+HMA+ADX ise isteğe bağlı HMA/ADX çizgileri de eklenebilir
              (Ayarlar &gt; Sinyal'deki checkbox'lar). Üstte periyot seçici ve "↺ Sıfırla" butonu,
              altta fare ile gezinince canlı güncellenen bir OHLC + gösterge şeridi (A/K%, Maks/Min%
              gibi mum istatistikleri dahil) bulunur.</>}</p>
        </Param>
        <Param name={isEn ? 'Right — Live Status' : 'Sağ — Canlı Durum'}>
          <p>{isEn
            ? 'Current price, trend direction (MA50 vs MA200), ADX/RSI info, and an "Activity Log" listing recent events — explains what the bot/strategy is currently thinking, in plain language.'
            : 'Anlık fiyat, trend yönü (MA50 vs MA200), ADX/RSI bilgisi ve son olayların listelendiği bir "Olay Günlüğü" — botun/stratejinin o an ne düşündüğünü Türkçe açıklar.'}</p>
        </Param>
        <Param name={isEn ? 'Bottom — Trade History' : 'Alt — İşlem Geçmişi'}>
          <p>{isEn
            ? 'Table of all past and open trades; filtering, CSV export, "📈" to open Trade Visualization, and "📊 Report" for a detailed analysis report.'
            : 'Geçmiş ve açık tüm işlemlerin tablosu; filtreleme, CSV indirme, "📈" ile İşlem Görselleştirmesi\'ni açma, ve "📊 Rapor" ile detaylı analiz raporuna geçiş.'}</p>
        </Param>
      </Section>

      <Section title={isEn ? '📑 Tabs (Above the Chart)' : '📑 Sekmeler (Grafik Üstü)'}>
        <p className="sh-text">{isEn ? 'A tab opens above the chart area for each pair you\'re interested in:' : 'Grafik alanının üstünde, ilgilendiğiniz her parite için bir sekme açılır:'}</p>
        <Timeline steps={isEn ? [
          { label: '⚙ Bot', text: 'A pair with a running bot — fixed, only closes via "Stop".', color: 'var(--accent)' },
          { label: '⚗ Bot (Test)', text: 'Same, but running in simulation mode — shown in purple.', color: '#a161d2' },
          { label: '● Watch', text: 'A tab watching an open trade (manual or bot) — SL/TP/trend tracking continues in the background.', color: '#3b82f6' },
          { label: '○ Trace', text: 'A pair you\'re just looking at, not yet trading — can be closed (with ×).', color: '#94a3b8' },
        ] : [
          { label: '⚙ Bot', text: 'Bir botun çalıştığı parite — sabittir, sadece "Durdur" ile kapanır.', color: 'var(--accent)' },
          { label: '⚗ Bot (Test)', text: 'Aynısı ama simülasyon modunda çalışan bot — mor renkle ayırt edilir.', color: '#a161d2' },
          { label: '● İzleme (Watch)', text: 'Açık bir işlemin (manuel ya da bot) izlendiği sekme — SL/TP/trend takibi arka planda devam eder.', color: '#3b82f6' },
          { label: '○ Trace', text: 'Sadece incelediğiniz, henüz işlem açmadığınız bir parite — kapatılabilir (× ile).', color: '#94a3b8' },
        ]} />
        <Note>
          {isEn
            ? 'The tab you\'re currently viewing is clearly highlighted with a dark background and a thick underline — so you never mix up which pair\'s chart you\'re looking at.'
            : 'O an görüntülediğiniz sekme, koyu bir arka plan ve kalın alt çizgiyle belirgin şekilde vurgulanır — hangi paritenin grafiğine baktığınızı karıştırmamanız için.'}
        </Note>
      </Section>

      <Section title={isEn ? '🧭 Top-Right Icon Bar' : '🧭 Üst Sağ İkon Çubuğu'}>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--muted)', lineHeight: 1.9 }}>
          <li><strong>▤ {isEn ? 'Logs' : 'Loglar'}</strong> — {isEn ? 'raw backend server logs (technical, advanced use).' : 'sunucu tarafındaki ham backend logları (teknik, gelişmiş kullanım).'}</li>
          <li><strong>📊 Backtest</strong> — {isEn ? 'Single Test, Parameter Sweep, History tabs (explained in their own section).' : 'Tekli Test, Parametre Taraması, Geçmiş sekmeleri (ayrı bölümde anlatılıyor).'}</li>
          <li><strong>🎧 {isEn ? 'Support' : 'Destek'}</strong> — {isEn
            ? 'open a support request for account/technical/billing/bot issues, write your subject and message, and chat back and forth with the support team on the same ticket until it\'s resolved — you can close it yourself once you\'re satisfied.'
            : 'hesap/teknik/ödeme/bot ile ilgili bir sorununuz için destek talebi açın, konu ve mesajınızı yazın, aynı talep üzerinden destek ekibiyle karşılıklı yazışın — sorun çözülünce talebi kendiniz kapatabilirsiniz.'}</li>
          <li><strong>❔ {isEn ? 'Help' : 'Yardım'}</strong> — {isEn ? 'the page you\'re reading right now.' : 'şu an okuduğunuz bu sayfa.'}</li>
          <li><strong>⚙ {isEn ? 'Settings' : 'Ayarlar'}</strong> — {isEn ? 'API keys, strategy selection, risk parameters, wallet transfer, etc.' : 'API anahtarları, strateji seçimi, risk parametreleri, cüzdan transferi vb.'}</li>
          <li><strong>→ {isEn ? 'Log out' : 'Çıkış'}</strong> — {isEn ? 'ends your session.' : 'oturumu kapatır.'}</li>
        </ul>
        <Note>
          {isEn
            ? 'On narrow screens (mobile/tablet) this icon bar collapses into a single ☰ button — clicking it lists the same options as a labeled dropdown menu.'
            : 'Dar ekranlarda (mobil/tablet) bu ikon çubuğu tek bir ☰ butonuna dönüşür — tıklayınca aynı seçenekler etiketli bir açılır menü olarak listelenir.'}
        </Note>
      </Section>

      <Section title={isEn ? '🖱 Dragging Windows' : '🖱 Pencereleri Sürükleme'}>
        <p className="sh-text">
          {isEn
            ? 'All popup windows — Settings, Backtest, Help, Reports, Logs, and Trade Visualization — can be dragged by their title bar. Handy if you want to keep multiple windows open in different positions at once.'
            : 'Ayarlar, Backtest, Yardım, Raporlar, Loglar ve İşlem Görselleştirmesi gibi tüm açılır pencereler başlık çubuğundan tutup sürüklenebilir — ekranda birden fazla pencereyi aynı anda farklı konumlarda açık tutmak isterseniz kullanışlıdır.'}
        </p>
      </Section>
    </>
  );
}

// ── 3. Manuel İşlem ───────────────────────────────────────────────────
function ManualTradingTab({ lang }) {
  const isEn = lang === 'en';
  return (
    <>
      <Section title={isEn ? '🎛 Manual Trading = 100% Your Control' : '🎛 Manuel İşlem = Tamamen Sizin Kontrolünüz'}>
        <p className="sh-text">
          {isEn
            ? <>Manual trades are <strong>not tied to any strategy or any timeframe (period)</strong> —
              whatever strategy is currently selected under Settings &gt; Signal does NOT affect your
              manual trade. There is no bot-style "dynamic TP/SL" or automatic trailing; the trade is
              entirely under your control and responsibility.</>
            : <>Manuel işlemler <strong>hiçbir stratejiye ve hiçbir zaman aralığına (periyoda) bağlı
              değildir</strong> — Ayarlar &gt; Sinyal'de o an hangi strateji seçili olursa olsun, manuel
              işleminizi ETKİLEMEZ. Botlardaki gibi bir "dinamik TP/SL" ya da otomatik trailing YOKTUR;
              işlem tamamen sizin kontrolünüz ve sorumluluğunuzdadır.</>}
        </p>
        <Warn>
          {isEn
            ? <>The only thing that doesn't change is the <strong>starting TP/SL price/percentage</strong> set
              at open (or computed from the account default if left blank) — and that only applies at the
              moment of opening. After that, it stays fixed unless YOU drag it on the chart; no
              background mechanism ever changes it on its own.</>
            : <>Değişmeyen tek şey, açılışta belirlenen (ya da hiç girilmezse hesap varsayılanından
              hesaplanan) <strong>başlangıç TP/SL fiyatı/yüzdesidir</strong> — ve o da SADECE açılış
              anına özeldir. Ondan sonrası, siz grafikte sürükleyip değiştirmediğiniz sürece, sabit
              kalır; hiçbir arka plan mekanizması onu kendiliğinden değiştirmez.</>}
        </Warn>
      </Section>

      <Section title={isEn ? '▲▼ Opening a Manual Trade' : '▲▼ Manuel İşlem Açma'}>
        <p className="sh-text">
          {isEn
            ? <>When you open a pair in the Trace tab, a "Manual Trade" box appears above the chart. Spot
              is a plain <strong>Buy/Sell</strong> market (not a "position" like Futures) — there's no
              "Long"/"Short" direction, so the buttons are labeled <strong>▶ Buy</strong> / <strong>▶ Buy
              (Test)</strong> to open, and once open, a red <strong>⏹ Sell</strong> button appears in the
              same spot to close it (shorting is technically impossible on Spot — no borrowing). On Futures
              there are four buttons instead:</>
            : <>Bir pariteyi Trace sekmesinde açtığınızda, grafiğin üstünde bir "Manuel İşlem" kutusu
              belirir. Spot düz bir <strong>Al/Sat</strong> piyasasıdır (Vadeli'deki gibi bir "pozisyon"
              değil) — "Long"/"Short" yönü yoktur, bu yüzden açmak için <strong>▶ Al</strong> / <strong>▶
              Al (Test)</strong>, açıldıktan sonra kapatmak için aynı yerde kırmızı bir <strong>⏹
              Sat</strong> butonu belirir (açığa satış/short teknik olarak mümkün değildir — borçlanma
              yoktur). Vadeli'de bunun yerine dört buton vardır:</>}
        </p>
        <Timeline steps={isEn ? [
          { label: '▲ Long / ▼ Short', text: 'Opens an instant market order with real money (requires subscription).', color: '#16a34a' },
          { label: '▲ Long (Test) / ▼ Short (Test)', text: 'Same logic but no order ever reaches OKX — simulation (no subscription needed, shown in purple).', color: '#a161d2' },
        ] : [
          { label: '▲ Long / ▼ Short', text: 'Gerçek para ile anında piyasa emri açar (abonelik gerektirir).', color: '#16a34a' },
          { label: '▲ Long (Test) / ▼ Short (Test)', text: 'Aynı mantık ama OKX\'e hiç emir gitmez — simülasyon (abonelik gerektirmez, mor renkle gösterilir).', color: '#a161d2' },
        ]} />
        <Note>{isEn ? 'Only ONE open trade at a time is allowed (per pair) — buttons lock while a trade is open.' : 'Aynı anda sadece BİR açık işlem olabilir (o parite için) — açık işlem varken butonlar kilitlenir.'}</Note>
        <Warn>
          {isEn
            ? <>On <strong>Futures</strong>, the margin you actually end up with can come out LOWER than the
              amount you typed. OKX only lets you trade in whole contracts, and each contract represents a
              fixed notional value (e.g. 0.01 BTC for BTC-USDT-SWAP) — your Margin×Leverage is converted to
              a contract count and rounded DOWN to the nearest whole contract, then the real margin is
              recalculated from that. At low leverage on an expensive pair, this rounding can be large (e.g.
              asking for 300 USDT margin at 3× on BTC can actually open ~209 USDT, because 300×3=900 isn't
              enough for a 2nd whole contract). If this matters to you, increase the leverage or the margin
              amount so your notional clears the next contract boundary — the exact opened size is always
              shown in "Open Manual Trades" and Trade History right after opening.</>
            : <><strong>Vadeli</strong>'de gerçekleşen marjin, girdiğiniz tutardan DAHA DÜŞÜK çıkabilir. OKX
              sadece TAM SAYIDA kontratla işlem yapmanıza izin verir, her kontrat sabit bir nominal değeri
              temsil eder (ör. BTC-USDT-SWAP için 0.01 BTC) — Marjin×Kaldıraç değeriniz kontrat sayısına
              çevrilir ve en yakın TAM kontrata AŞAĞI yuvarlanır, gerçek marjin de bu sayıdan geriye doğru
              hesaplanır. Düşük kaldıraçta pahalı bir paritede bu yuvarlama büyük olabilir (ör. BTC'de 3x'te
              300 USDT marjin istemek aslında ~209 USDT açabilir, çünkü 300×3=900, 2. tam kontrata yetmez).
              Bu sizin için önemliyse kaldıracı ya da marjin tutarını artırıp nominal değerinizin bir
              sonraki kontrat sınırını aşmasını sağlayın — gerçekte açılan tutar her zaman "Açık Manuel
              İşlemler" ve İşlem Geçmişi'nde açılış anında gösterilir.</>}
        </Warn>
        <Warn>
          {isEn
            ? <>On Spot, the "Amount" field's unit is the pair's actual QUOTE currency, which is
              NOT always USDT: for BTCUSDT (quote=USDT), Buy spends USDT to acquire BTC, Sell sells BTC
              back to USDT. But for USDTTRY (quote=TRY, USDT is the BASE here), Buy spends TRY to
              acquire USDT, and Sell sells that USDT back to TRY. The field's unit label always reflects
              the correct currency for the pair you typed.</>
            : <>Spot'ta "Miktar" alanının birimi paritenin GERÇEK quote (karşı) para birimidir, bu HER
              ZAMAN USDT değildir: BTCUSDT'de (quote=USDT) Al, USDT harcayıp BTC alır; Sat, BTC'yi satıp
              USDT'ye döner. Ama USDTTRY'de (quote=TRY, USDT burada BASE'tir) Al, TRY harcayıp USDT alır;
              Sat, o USDT'yi satıp TRY'ye döner. Alandaki birim etiketi her zaman girdiğiniz paritenin
              doğru para birimini gösterir.</>}
        </Warn>
      </Section>

      <Section title={isEn ? '🎯 Setting TP/SL Before Opening (optional)' : '🎯 Açılıştan Önce TP/SL Belirleme (opsiyonel)'}>
        <p className="sh-text">
          {isEn
            ? <>Entry always happens IMMEDIATELY at the market price the instant you click — TP/SL is a
              completely separate concept and never delays or blocks the entry itself. Next to the button
              there's a small "SL" and "TP" field. For each, SL and TP are picked <strong>independently</strong>
              — one can be "Amount" while the other is "%", there's no requirement that they match. You can
              choose one of three modes:</>
            : <>Giriş (alım) tıkladığınız anda HER ZAMAN piyasa fiyatından anında gerçekleşir — TP/SL
              tamamen ayrı bir kavramdır, girişi asla geciktirmez ya da engellemez. Butonun yanında küçük
              bir "SL" ve "TP" alanı vardır. SL ve TP modu <strong>birbirinden bağımsız</strong> seçilir —
              biri "Tutar" iken diğeri "%" olabilir, ikisinin aynı mod olması gerekmez. Her biri için üç
              moddan birini seçebilirsiniz:</>}
        </p>
        <Param name={isEn ? 'Percent (%)' : 'Yüzde (%)'}>
          <p>{isEn ? 'You write how far from the entry price it should sit, as a percentage (e.g. SL: 1 → 1% below/above entry, depending on direction).' : 'Giriş fiyatından yüzde kaç uzakta durması gerektiğini yazarsınız (örn. SL: 1 → giriş fiyatının %1 altı/üstü, yöne göre).'}</p>
        </Param>
        <Param name={isEn ? 'Price' : 'Fiyat'}>
          <p>{isEn
            ? 'You write the exact price value directly (e.g. TP: 65000). Since the trade opens at the market price, your TP/SL must be consistent with the direction (for Long, SL below entry, TP above — an inconsistent value is rejected by the system).'
            : 'Tam fiyat değerini doğrudan yazarsınız (örn. TP: 65000). İşlem piyasa fiyatından açılacağı için, girdiğiniz TP/SL\'in yönle tutarlı olması gerekir (Long\'da SL girişin altında, TP üstünde olmalı — tutarsız bir değer sistem tarafından reddedilir).'}</p>
        </Param>
        <Param name={isEn ? 'Amount' : 'Tutar'}>
          <p>{isEn
            ? <>You write a plain USDT risk/profit amount (e.g. SL: 5 → this trade should stop out at
              exactly $5 of loss). The system converts this to a price using the REAL notional value of the
              position: on Futures that's actual margin × leverage, on Spot it's simply the USDT amount you
              spent. Example: 100 USDT margin at 5× (notional 500) with SL: 5 → the price distance is 1% of
              entry (5/500), because a 1% adverse move on a 500 USDT notional is exactly $5.</>
            : <>Düz bir USDT risk/kâr tutarı yazarsınız (örn. SL: 5 → bu işlem tam 5$ zararda stop olsun).
              Sistem bunu, pozisyonun GERÇEK nominal değerini kullanarak fiyata çevirir: Vadeli'de bu gerçek
              marjin × kaldıraçtır, Spot'ta ise sadece harcadığınız USDT tutarıdır. Örnek: 5x kaldıraçta 100
              USDT marjin (nominal 500) ile SL: 5 → fiyat mesafesi girişin %1'i olur (5/500), çünkü 500
              USDT'lik nominal üzerinde %1'lik ters hareket tam olarak 5$'dır.</>}</p>
        </Param>
        <Note>
          {isEn
            ? <>On <strong>Futures</strong>, both fields are optional — if left blank, your account default
              under Settings &gt; Risk (ATR-based or the percentage you set there) is applied automatically.</>
            : <>Vadeli'de her iki alan da <strong>opsiyoneldir</strong> — boş bırakırsanız Ayarlar &gt;
              Risk'teki hesap varsayılanınız (ATR-bazlı ya da orada girdiğiniz yüzde) otomatik uygulanır.</>}
        </Note>
        <Warn>
          {isEn
            ? <>On <strong>Spot</strong>, leaving BOTH SL and TP blank means something different: <strong>no
              SL/TP tracking at all</strong> — no account default is applied, no reward:risk check runs, the
              trade is simply a plain market buy with nothing attached. This ONLY applies to Spot manual
              trades; on Futures, blank still falls back to your account default as above.</>
            : <><strong>Spot</strong>'ta hem SL hem TP'yi boş bırakmak farklı bir anlama gelir: <strong>hiç
              SL/TP takibi yapılmaz</strong> — hesap varsayılanı uygulanmaz, ödül:risk kontrolü çalışmaz,
              işlem sadece hiçbir şey iliştirilmemiş düz bir piyasa alımı olur. Bu SADECE Spot manuel
              işlemler için geçerlidir; Vadeli'de boş bırakmak yukarıdaki gibi hesap varsayılanına düşmeye
              devam eder.</>}
        </Warn>
      </Section>

      <Section title={isEn ? '🖱 After Opening: Drag-and-Drop TP/SL on the Chart' : '🖱 Açıldıktan Sonra: Grafikte Sürükle-Bırak ile TP/SL Değiştirme'}>
        <p className="sh-text">
          {isEn
            ? 'While a manual trade is open, small colored handles appear on the right edge of the TP and SL lines on the chart (green "TP", red "SL"). Grabbing this handle and dragging it up/down and releasing:'
            : 'Manuel bir işlem açıkken, grafikte TP ve SL çizgilerinin sağ kenarında küçük renkli tutamaçlar (yeşil "TP", kırmızı "SL") belirir. Bu tutamacı fare ile tutup yukarı/aşağı sürükleyip bıraktığınızda:'}
        </p>
        <Timeline steps={isEn ? [
          { label: 'While dragging', text: 'The handle follows your cursor, showing a live price preview — nothing is saved yet.', color: '#64748b' },
          { label: 'On release', text: 'The new price is sent to the server: if it\'s a real trade, the SL/TP order on OKX is amended too, and the database is updated.', color: 'var(--accent)' },
          { label: 'An invalid value', text: 'If it violates direction logic (e.g. SL above entry on a Long), the server rejects it and shows an error message.', color: '#dc2626' },
        ] : [
          { label: 'Sürüklerken', text: 'Tutamaç fareyi takip eder, üzerinde anlık fiyat önizlemesi gösterilir — henüz hiçbir şey kaydedilmez.', color: '#64748b' },
          { label: 'Bıraktığınızda', text: 'Yeni fiyat sunucuya gönderilir: gerçek bir işlemse OKX\'teki SL/TP emri de amend edilir, veritabanı güncellenir.', color: 'var(--accent)' },
          { label: 'Hatalı bir değer', text: 'Yön mantığına aykırıysa (ör. Long\'da SL girişin üstünde) sunucu reddeder ve bir hata mesajı gösterilir.', color: '#dc2626' },
        ]} />
        <Warn>
          {isEn
            ? <>This feature works ONLY for manual trades. On a bot-managed trade (⚙ Bot tab), no draggable
              handle ever appears on the chart — the bot's TP/SL is managed automatically by its own
              strategy and the user can never interfere with it. This is a deliberate safety boundary.</>
            : <>Bu özellik SADECE manuel işlemlerde çalışır. Bot'un yönettiği bir işlemde (⚙ Bot sekmesi)
              grafikte hiçbir sürüklenebilir tutamaç görünmez — botun TP/SL'i kendi stratejisine göre
              otomatik yönetilir, kullanıcı buna asla müdahale edemez. Bu kasıtlı bir güvenlik sınırıdır.</>}
        </Warn>
      </Section>

      <Section title={isEn ? '⏹ Closing a Manual Trade — Accessible From 3 Places' : '⏹ Manuel İşlemi Kapatma — 3 Yerden Erişilebilir'}>
        <p className="sh-text">{isEn ? 'There are three equivalent ways to instantly close (at market price) an open manual trade:' : 'Açık bir manuel işlemi anında (piyasa fiyatından) kapatmak için üç eşdeğer yol vardır:'}</p>
        <Timeline steps={isEn ? [
          { label: 'Above the chart', text: 'The red "⏹ Close" button that appears in the Manual Trade box, on that pair\'s Trace tab.', color: '#dc2626' },
          { label: 'Left panel', text: 'The "Close" button on the "Open Manual Trades" card (right below Active Bots) — along with pair/side/SL/TP info.', color: 'var(--accent)' },
          { label: 'Trade History', text: 'The "Close" button next to the open row in the table below.', color: '#64748b' },
        ] : [
          { label: 'Grafik üstü', text: 'İlgili paritenin Trace sekmesinde, Manuel İşlem kutusunun içinde beliren kırmızı "⏹ Kapat" butonu.', color: '#dc2626' },
          { label: 'Sol panel', text: '"Açık Manuel İşlemler" kartındaki (Aktif Botlar\'ın hemen altında) "Kapat" butonu — parite/yön/SL/TP bilgisiyle birlikte.', color: 'var(--accent)' },
          { label: 'İşlem Geçmişi', text: 'Alt tablodaki açık satırın yanındaki "Kapat" butonu.', color: '#64748b' },
        ]} />
        <p className="sh-text">
          {isEn
            ? 'The resulting profit/loss and closing reason are recorded at closing time; you can read this later in the "Description" section of Trade Visualization.'
            : 'Kapatma anında gerçekleşen kâr/zarar ve kapanış sebebi kaydedilir; bu bilgiyi daha sonra İşlem Görselleştirmesi\'ndeki "Açıklama" bölümünde tekrar okuyabilirsiniz.'}
        </p>
      </Section>
    </>
  );
}

// ── 4. Bot İşlemleri ──────────────────────────────────────────────────
function BotTab({ lang }) {
  const isEn = lang === 'en';
  return (
    <>
      <Section title={isEn ? '▶ How to Start a Bot' : '▶ Bot Nasıl Başlatılır'}>
        <p className="sh-text">
          {isEn
            ? <>In the Trace tab, next to the Manual Trade box, there's a "Period" selector and two
              buttons: <strong>"▶ Start"</strong> (real) and <strong>"▶ Start (Test)"</strong> (purple,
              simulation, no subscription needed). The period is EXPLICITLY chosen here — completely
              independent from the period dropdown on the chart; whatever period the bot starts with, it
              stays on that period for its whole lifetime.</>
            : <>Trace sekmesinde, Manuel İşlem kutusunun yanında bir "Periyot" seçici ve iki buton vardır:
              <strong> "▶ Başlat"</strong> (gerçek) ve <strong>"▶ Başlat (Test)"</strong> (mor, simülasyon,
              abonelik gerektirmez). Periyot burada AÇIKÇA seçilir — grafikteki periyot dropdown'ından
              tamamen bağımsızdır; bot hangi periyotla başlarsa ömrü boyunca o periyotta kalır.</>}
        </p>
        <Warn>
          {isEn
            ? <><strong>Locking rule:</strong> The moment a bot starts, ALL parameters currently under
              Settings &gt; Signal (strategy) and Settings &gt; Risk are locked as a "snapshot". Even if
              you change these values in Settings while the bot is running, the RUNNING bot does NOT
              reflect it — the change only applies to bots started AFTER that. The bot keeps its locked
              settings even across a server restart.</>
            : <><strong>Kilitlenme kuralı:</strong> Bot başladığı anda, o anki Ayarlar &gt; Sinyal (strateji)
              ve Ayarlar &gt; Risk'teki TÜM parametreler bir "anlık görüntü" (snapshot) olarak kilitlenir.
              Bot çalışırken Ayarlar'dan bu değerleri değiştirseniz bile, ÇALIŞMAKTA OLAN bot bunu
              YANSITMAZ — değişiklik sadece bundan SONRA başlatılacak botlara uygulanır. Sunucu yeniden
              başlasa bile bot bu kilitli ayarlarla devam eder.</>}
        </Warn>
        <Note>
          {isEn
            ? 'Running bots and open manual trades share the SAME total slot pool (unsubscribed: 1, subscribed: 4) — see the "How Many Bots/Manual Trades Can I Run at Once?" section under "Getting Started" for the full rules.'
            : 'Çalışan botlar ile açık manuel işlemler AYNI toplam slot havuzunu paylaşır (abonesiz: 1, abone: 4) — tam kurallar için "Başlarken" sekmesindeki "Aynı Anda Kaç Bot/Manuel İşlem Açabilirim?" bölümüne bakın.'}
        </Note>
      </Section>

      <Section title={isEn ? '⏹ How to Stop a Bot' : '⏹ Bot Nasıl Durdurulur'}>
        <p className="sh-text">
          {isEn
            ? <>Click the <strong>"■ [PAIR] Stop"</strong> button that appears at the top while the Bot tab
              is selected. Once stopped, if there's an open position at that moment, it is
              <strong> automatically closed</strong> — an open position is never left without a bot.</>
            : <>Bot sekmesi seçiliyken üstte beliren <strong>"■ [PARİTE] Durdur"</strong> butonuna basın.
              Bot durdurulunca, o an açık bir pozisyon varsa <strong>otomatik olarak kapatılır</strong> —
              açık pozisyon botsuz bırakılmaz.</>}
        </p>
      </Section>

      <Section title={isEn ? '🛡 The Bot\'s Automatic Protection Mechanisms' : '🛡 Botun Otomatik Koruma Mekanizmaları'}>
        <Param name="SL Cooldown" unit={isEn ? 'minutes' : 'dakika'}>
          <p>{isEn ? 'Once a Stop Loss triggers, no new trade opens on the same pair for this long — to prevent chained (back-to-back) losses. Not available for RSI+SMA / SMA-50 — for these 2, a new trade can open again immediately.' : 'Bir Stop Loss tetiklenince, aynı paritede bu süre boyunca yeni işlem açılmaz — zincirleme (art arda) kayıpları önlemek için. RSI+SMA / SMA-50\'de bu alan yoktur — bu 2 stratejide yeni işlem hemen açılabilir.'}</p>
        </Param>
      </Section>

      <Note>
        {isEn
          ? 'Test bots (⚗) run the EXACT SAME decision logic as real bots (⚙) — the only difference is that no real order is sent to OKX. This lets you safely observe a strategy before trying it with real money.'
          : 'Test botları (⚗) gerçek botlarla (⚙) TAMAMEN aynı karar mantığını çalıştırır — tek fark OKX\'e gerçek emir gönderilmemesidir. Bu, bir stratejiyi gerçek parayla denemeden önce güvenle gözlemlemenizi sağlar.'}
      </Note>
    </>
  );
}

// ── 5. Stratejiler ────────────────────────────────────────────────────
function StrategiesTab({ lang }) {
  const isEn = lang === 'en';
  return (
    <>
      <Section title={isEn ? '📋 6 Strategies — Quick Summary' : '📋 6 Strateji — Kısa Özet'}>
        <p className="sh-text">
          {isEn
            ? <>You choose which strategy runs from Settings &gt; Signal. For the most detailed info on
              EVERY parameter of each strategy (range/recommended value/effect), see the
              <strong> "❓ Help"</strong> button under Backtest &gt; Parameter Sweep — here we just briefly
              explain HOW each strategy decides.</>
            : <>Ayarlar &gt; Sinyal sekmesinden hangi stratejinin çalışacağını seçersiniz. Her stratejinin
              TÜM parametreleri (aralık/önerilen değer/etkisi) hakkında en detaylı bilgi için Backtest &gt;
              Parametre Taraması sekmesindeki <strong>"❓ Yardım"</strong> butonuna bakın — burada sadece
              her stratejinin NASIL karar verdiğini kısaca anlatıyoruz.</>}
        </p>
      </Section>

      <Section title="📈 Range Bar + HMA + ADX">
        <p className="sh-text">
          {isEn
            ? <>A state-based, always-in-market trend-following strategy that works on "range bars" —
              formed when price moves a certain box size — instead of raw candles (reduces noise).
              Whenever flat, it immediately opens a position in the current Hull MA (HMA) direction — it
              never waits for a flip. ADX (optional) filters trend strength. <strong>There is no fixed
              TP</strong>, only a safety-net SL; when the HMA direction reverses, the position closes and
              immediately reopens in the opposite direction (on futures).</>
            : <>Durum-tabanlı, sürekli piyasada kalan bir trend-takip stratejisi — ham mum yerine
              fiyatın belirli bir kutu büyüklüğü kadar hareket ettiğinde oluşan "range bar"lar üzerinde
              çalışır (gürültüyü azaltır). Flat kaldığı HER an, o anki Hull MA (HMA) yönüne göre hemen
              pozisyon açar — flip beklemez. ADX (isteğe bağlı) trend gücünü filtreler. <strong>Sabit TP
              yoktur</strong>, sadece yedek sigorta SL vardır; HMA yönü tersine dönünce pozisyon kapanır
              ve aynı anda ters yönde yeniden açılır (vadelide).</>}
        </p>
      </Section>

      <Section title="📊 RSI + SMA">
        <p className="sh-text">
          {isEn
            ? 'Entry is unconditional and instant: LONG if RSI is above its own moving average (SMA), SHORT if below — flips to that side whenever flat. TP and SL are both optional (Risk tab), each independently OFF at 0/blank — unlike other strategies, there is no ATR fallback here, so a 0/blank SL leaves the position with no fixed price protection at all.'
            : 'Giriş koşulsuz ve anlıktır: RSI, kendi hareketli ortalamasının (SMA) üzerindeyse LONG, altındaysa SHORT — flat kaldığı her an bu yöne döner. TP ve SL ikisi de opsiyoneldir (Risk sekmesi), her biri bağımsız olarak 0/boş iken KAPALIDIR — diğer stratejilerin aksine burada ATR fallback\'ı yoktur, yani SL 0/boş bırakılırsa pozisyonun hiçbir sabit fiyat koruması olmaz.'}
        </p>
      </Section>

      <Section title="📊 SMA - 50 Level">
        <p className="sh-text">
          {isEn
            ? <>Compares SMA (the 14-period average of RSI) to a <strong>Dead Zone band</strong> around 50,
              deciding only at candle close — never on the live/intra-candle price. Entry requires SMA to be
              OUTSIDE this band (not just cross 50): LONG above 50+Dead&nbsp;Zone, SHORT below 50−Dead&nbsp;Zone
              — and this applies instantly at bot startup too, no waiting. <strong>Simple by design:</strong> the
              position just rides for as long as SMA stays outside the band, and closes the instant it returns
              inside (the exact mirror of the entry rule). <strong>TP and SL (Risk tab) are both optional</strong>,
              each independently OFF at 0/blank — unlike other strategies, there is no ATR fallback here, so a
              0/blank SL leaves the position with no fixed price protection at all.</>
            : <>SMA'yı (RSI'ün 14 periyotluk ortalaması) 50 etrafındaki bir <strong>Ölü Bölge bandıyla</strong>
              karşılaştırır, sadece mum kapanışında karar verir — anlık/mum-içi fiyata asla bakmaz. Giriş için
              SMA'nın bu bandın DIŞINDA olması gerekir (sadece 50'yi geçmesi değil): 50+Ölü&nbsp;Bölge üzerinde
              LONG, 50−Ölü&nbsp;Bölge altında SHORT — bu, bot açılışında da anında, bekleme olmadan uygulanır.
              <strong> Bilinçli olarak basit:</strong> pozisyon, SMA bandın dışında kaldığı sürece sürer, bandın
              içine döner dönmez kapanır (giriş kuralının tam simetriği). <strong>TP ve SL (Risk sekmesi) ikisi de
              opsiyoneldir</strong>, her biri bağımsız olarak 0/boş iken KAPALIDIR — diğer stratejilerin aksine
              burada ATR fallback'ı yoktur, yani SL 0/boş bırakılırsa pozisyonun hiçbir sabit fiyat koruması olmaz.</>}
        </p>
      </Section>

      <Section title="📐 Trend-Pullback + R">
        <p className="sh-text">
          {isEn
            ? <>Trend filter is MA50 vs MA200. Opens a trade when price is close enough to MA50 (a
              "pullback" — not overextended) and RSI momentum confirms. <strong>Simple by design:</strong> there's
              no fixed TP and no trailing yet (profit optimization is intentionally left for a future
              iteration) — the position simply rides for as long as the trend filter stays valid. If
              MA50/MA200 cross to the other side (trend broken), the position is closed immediately for
              safety. The fixed SL remains active in parallel as a backstop.</>
            : <>Trend filtresi MA50 vs MA200'dür. Fiyat MA50'ye yeterince yakınken (bir "pullback" —
              aşırı uzamamış an) ve RSI momentum onayı varsa işlem açılır. <strong>Bilinçli olarak basit:</strong> sabit
              bir TP yoktur, henüz trailing de yoktur (kâr optimizasyonu bilinçli olarak sonraki bir
              aşamaya bırakıldı) — pozisyon, trend filtresi geçerli kaldığı sürece sürer. MA50/MA200 karşı
              tarafa geçerse (trend kırıldıysa) pozisyon anında güvenlik amaçlı kapatılır. Sabit SL,
              paralelde yedek sigorta olarak aktif kalır.</>}
        </p>
      </Section>
    </>
  );
}

// ── 6. Ayarlar ────────────────────────────────────────────────────────
function SettingsTab({ lang }) {
  const isEn = lang === 'en';
  return (
    <>
      <Section title={isEn ? '🔐 API (OKX Connection)' : '🔐 API (OKX Bağlantısı)'}>
        <p className="sh-text">
          {isEn
            ? <><strong>Base URL</strong> (at the top) picks which OKX platform you're connecting to — pick
              it FIRST, then enter that platform's own API Key, Secret Key and Passphrase (created from the
              <strong> API Management</strong> page in that OKX account). Global and TR are entirely
              separate platforms/accounts, so each keeps its <strong>own, independently saved</strong>
              credential set — switching the dropdown back and forth never overwrites the other one.</>
            : <>(En üstteki) <strong>Base URL</strong>, hangi OKX platformuna bağlandığınızı seçer — ÖNCE
              onu seçin, sonra o platformun kendi <strong>API Yönetimi</strong> sayfasından oluşturduğunuz
              API Key, Secret Key ve Passphrase'ini girin. Global ve TR tamamen ayrı platform/hesaplardır,
              bu yüzden her birinin kimlik bilgisi <strong>kendi başına, ayrı ayrı</strong> saklanır —
              dropdown'ı ileri geri değiştirmek diğerinin bilgilerini SİLMEZ.</>}
        </p>
        <Param name="OKX Global" recommended={isEn ? 'default' : 'varsayılan'}>
          <p><code>www.okx.com</code> — {isEn ? 'pick this if your account is on OKX\'s general/international platform. Supports both Spot and Futures.' : 'hesabınız OKX\'in genel/uluslararası platformundaysa bunu seçin. Hem Spot hem Vadeli destekler.'}</p>
        </Param>
        <Param name="OKX TR">
          <p><code>tr.okx.com</code> — {isEn ? 'pick this if your account was opened via OKX Turkey.' : 'hesabınız OKX Türkiye üzerinden açıldıysa bunu seçin.'}</p>
        </Param>
        <Warn>
          {isEn
            ? <>OKX TR does <strong>NOT</strong> offer futures/derivatives trading — only Spot. When it's
              active, the "Futures" market button in Bot Settings is automatically disabled and locked to
              Spot.</>
            : <>OKX TR vadeli/türev işlem <strong>SUNMAZ</strong> — sadece Spot. O seçiliyken Bot
              Ayarları'ndaki "Vadeli" piyasa butonu otomatik olarak devre dışı bırakılır, Spot'a kilitlenir.</>}
        </Warn>
        <p className="sh-text">
          {isEn ? 'Once saved, existing connections update IMMEDIATELY with the new key/region.' : 'Kaydedilince mevcut bağlantılar HEMEN bu yeni anahtar/bölge ile güncellenir.'}
        </p>
        <Note>
          {isEn
            ? <>The left panel always shows <strong>"Active Exchange"</strong> under your name — the
              platform currently in use, verified with a real balance check ("-" = no key entered yet,
              "Invalid API Credentials" = key entered but rejected by OKX).</>
            : <>Sol paneldeki isminizin altında her zaman <strong>"Aktif Borsa"</strong> yazar — o an
              kullanılan platform, gerçek bir bakiye sorgusuyla doğrulanır ("-" = henüz anahtar
              girilmemiş, "Geçersiz API Bilgileri" = anahtar girilmiş ama OKX tarafından reddedilmiş).</>}
        </Note>
        <Warn>
          {isEn
            ? <>Base URL can NOT be changed while you have an active bot or open trade — this would
              silently redirect it to the wrong platform mid-flight. Stop your bots / close your trades
              first, then switch.</>
            : <>Aktif bir botunuz veya açık işleminiz varken Base URL DEĞİŞTİRİLEMEZ — bu, işlemi
              çalışırken sessizce yanlış platforma yönlendirirdi. Önce botları durdurun / işlemleri
              kapatın, sonra değiştirin.</>}
        </Warn>
        <Warn>{isEn ? 'Grant your API key ONLY "Trade" permission, NEVER enable withdrawal permission.' : 'API anahtarınıza SADECE "İşlem" (trade) izni verin, çekim (withdraw) iznini ASLA açmayın.'}</Warn>
      </Section>

      <Section title={isEn ? '📡 Signal (Strategy Selection)' : '📡 Sinyal (Strateji Seçimi)'}>
        <p className="sh-text">
          {isEn
            ? <>One of the buttons under "Analysis Strategy" selects which strategy the bot/analysis
              engine decides with (see the Strategies section). Below it, that strategy's full rule set
              (LONG/SHORT conditions) is shown automatically based on your selection. The Range
              Bar+HMA+ADX strategy also has "HMA Indicator"/"ADX Indicator" checkboxes — these ONLY show/hide the line on the
              chart, they don't affect decision logic.</>
            : <>"Analiz Stratejisi" başlığı altındaki butonlardan biriyle botun/analiz motorunun hangi
              stratejiyle karar vereceğini seçersiniz (bkz. Stratejiler bölümü). Seçime göre altta o
              stratejinin tam kural seti (LONG/SHORT koşulları) otomatik gösterilir. Range Bar+HMA+ADX
              stratejisinde ayrıca "HMA İndikatör"/"ADX İndikatör" checkbox'ları vardır — bunlar SADECE
              grafikte çizgiyi gösterip gizler, karar mantığını etkilemez.</>}
        </p>
      </Section>

      <Section title={isEn ? '🛡 Risk' : '🛡 Risk'}>
        <p className="sh-text">
          {isEn
            ? 'TP/SL calculation mode (ATR-multiplier or fixed percentage), ATR period, SL Cooldown, and parameters specific to the selected strategy (HMA period, ADX threshold, RSI thresholds, etc.) are configured here. Only fields RELEVANT to the selected strategy are shown. These values are used as defaults for newly opened bots/manual trades — they don\'t affect a RUNNING bot (see above). The CHOP (sideways market) filter, Min. Reward:Risk, and Daily Loss Limit are no longer user-configurable — they always use their safe defaults (CHOP filter on at 61.8, the other two off).'
            : 'TP/SL hesaplama modu (ATR-çarpanı ya da sabit yüzde), ATR periyodu, SL Cooldown ve seçili stratejiye özel parametreler (HMA periyodu, ADX eşiği, RSI eşikleri vb.) burada ayarlanır. Sadece seçili stratejiyle İLGİLİ alanlar görünür. Bu değerler yeni açılacak bot/manuel işlemlerde varsayılan olarak kullanılır — ÇALIŞMAKTA olan bir botu etkilemez (yukarıya bakın). CHOP (yatay piyasa) filtresi, Min. Ödül:Risk ve Günlük Kayıp Limiti artık kullanıcı tarafından ayarlanamıyor — her zaman güvenli varsayılan değerleriyle çalışır (CHOP filtresi 61.8 ile açık, diğer ikisi kapalı).'}
        </p>
        <Note>
          {isEn
            ? 'For the exact meaning, value range, and effect of every parameter, see the "? Help" button (Risk Management Guide) under ⚙ Settings > Risk.'
            : 'Her parametrenin tam anlamı, değer aralığı ve etkisi için ⚙ Ayarlar > Risk sekmesindeki "? Yardım" butonuna (Risk Yönetimi Rehberi) bakın.'}
        </Note>
      </Section>

      <Section title={isEn ? '💰 Wallet' : '💰 Cüzdan'}>
        <p className="sh-text">
          {isEn
            ? <>This tab first lists ALL assets you currently hold across your Funding and Trading
              accounts, combined per currency (not just USDT — ETH, BTC, TRY, whatever you have;
              only non-zero amounts are shown). Below that is the <strong>USDT ⇄ TRY Convert</strong>
              tool.</>
            : <>Bu sekme önce Fon ve İşlem hesabınızda o an elinizde olan TÜM varlıkları, para birimi
              bazında birleştirerek listeler (sadece USDT değil — ETH, BTC, TRY, ne varsa; sadece
              "0"dan farklı olanlar gösterilir). Altında ise <strong>USDT ⇄ TRY Dönüştür</strong>
              aracı bulunur.</>}
        </p>
        <Param name={isEn ? 'USDT ⇄ TRY Convert' : 'USDT ⇄ TRY Dönüştür'}>
          <p>{isEn
            ? <>USDTTRY is NOT a profit-seeking "position" like BTCUSDT or BTCTRY — it's simply the
              instrument for converting between TRY and USDT within your Trading account. Drag the
              slider (or use the %25/%50/%75/%100 buttons) to pick how much of your source balance
              to convert, then click "Convert". This places a plain spot market order (no SL/TP,
              never shown in Trade History) at the current rate. (Trading USDTTRY as a regular pair
              for FX speculation is still possible from the Trace tab if you want that — this tool is
              just the simple, dedicated way to convert.)</>
            : <>USDTTRY, BTCUSDT ya da BTCTRY gibi kâr amaçlı bir "pozisyon" DEĞİLDİR — İşlem
              hesabınızdaki TRY ile USDT arasında dönüşüm yapmanın basit aracıdır. Kaynak bakiyenizin
              ne kadarını dönüştürmek istediğinizi sürükleyerek (ya da %25/%50/%75/%100 butonlarıyla)
              seçip "Dönüştür" dersiniz. Bu, güncel kurdan düz bir spot piyasa emri verir (SL/TP yok,
              İşlem Geçmişi'nde hiç görünmez). (USDTTRY'yi FX spekülasyonu için normal bir parite gibi
              Trace sekmesinden işlem yapmak isterseniz bu da hâlâ mümkün — bu araç sadece basit,
              amaca özel dönüştürme yoludur.)</>}</p>
        </Param>
        <Warn>
          {isEn
            ? 'Before opening a manual/bot trade (spot or futures alike), make sure your money is in the "Trading" account — ALL orders are funded from there, never from "Funding". If your balance is sitting in "Funding" instead, move it to "Trading" from OKX\'s own website/app first (TrendSniper does not offer a Funding⇄Trading transfer button).'
            : 'Manuel/bot işlem açmadan önce (spot ya da vadeli fark etmeksizin) paranızın "İşlem" hesabında olduğundan emin olun — TÜM emirler oradan karşılanır, "Fon"dan asla. Bakiyeniz "Fon"da duruyorsa önce OKX\'in kendi web sitesinden/uygulamasından "İşlem"e taşıyın (TrendSniper Fon⇄İşlem transfer butonu sunmuyor).'}
        </Warn>
      </Section>

      <Section title={isEn ? '👥 Users (system/admin accounts only)' : '👥 Kullanıcılar (sadece sistem/admin hesapları)'}>
        <p className="sh-text">
          {isEn
            ? 'System admins can search/filter all users here (subscription status, active/blocked), perform bulk actions (activate, block, change subscription status, delete), and individually edit a user\'s name, subscription start/end date, account status, admin notes, and reset their password. System-wide Admin/Viewer login credentials are also managed here.'
            : 'Sistem yöneticileri burada tüm kullanıcıları arayıp filtreleyebilir (abonelik durumu, aktif/engelli), toplu işlem yapabilir (aktive et, engelle, abonelik durumu değiştir, sil), ve tek tek bir kullanıcının adını, abonelik başlangıç/bitiş tarihini, hesap durumunu, admin notunu düzenleyebilir, şifresini sıfırlayabilir. Ayrıca sistem genelindeki Admin/Viewer giriş bilgileri de burada yönetilir.'}
        </p>
      </Section>
    </>
  );
}

// ── 7. Backtest ───────────────────────────────────────────────────────
function BacktestTab({ lang }) {
  const isEn = lang === 'en';
  return (
    <>
      <Section title={isEn ? '📊 What Is Backtest' : '📊 Backtest Nedir'}>
        <p className="sh-text">
          {isEn
            ? 'Lets you test how a strategy would have performed historically, using OKX\'s REAL historical candle data, without risking real money. It has three tabs:'
            : 'Bir stratejiyi gerçek para riske etmeden, OKX\'in GERÇEK geçmiş mum verisiyle geçmişte nasıl performans göstereceğini test etmenizi sağlar. Üç sekmesi vardır:'}
        </p>
        <Timeline steps={isEn ? [
          { label: 'Single Test', text: 'Runs one backtest over your chosen date range, with a single parameter combination.', color: '#3b82f6' },
          { label: 'Parameter Sweep', text: 'Tries multiple parameter values (comma-separated) at once (up to 60 combinations), and ranks the results.', color: 'var(--accent)' },
          { label: 'History', text: 'Lists all backtests you\'ve run before; detail/delete/verify are done from here.', color: '#64748b' },
        ] : [
          { label: 'Tekli Test', text: 'Tek bir parametre kombinasyonuyla, belirlediğiniz tarih aralığında tek bir backtest çalıştırır.', color: '#3b82f6' },
          { label: 'Parametre Taraması', text: 'Birden çok parametre değerini virgülle ayırıp aynı anda dener (en fazla 60 kombinasyon), sonuçları sıralar.', color: 'var(--accent)' },
          { label: 'Geçmiş', text: 'Daha önce çalıştırdığınız TÜM backtest\'lerin kaydını listeler; detay/silme/doğrulama buradan yapılır.', color: '#64748b' },
        ]} />
        <Note>
          {isEn
            ? <>The <strong>"❓ Help"</strong> button under the Parameter Sweep tab is a separate, very
              detailed guide explaining every parameter's full value range, recommended value, and effect,
              strategy by strategy — be sure to check it.</>
            : <>Parametre Taraması sekmesindeki <strong>"❓ Yardım"</strong> butonu, TÜM parametrelerin tam
              değer aralığını, önerilen değerini ve etkisini strateji strateji açıklayan ayrı ve çok
              detaylı bir rehberdir — mutlaka bakın.</>}
        </Note>
      </Section>

      <Section title={isEn ? '✓ Result Verification (OKX Data-Based)' : '✓ Sonuç Doğrulama (OKX Veri-Bazlı)'}>
        <p className="sh-text">
          {isEn
            ? <>The <strong>"✓ Verify with OKX Data"</strong> button next to a backtest result automatically
              checks whether EVERY trade's entry/exit price in that run falls within the real [low, high]
              range of the candle it happened on — 100% deterministic, free, no AI involved. Results are
              collected under Backtest &gt; History &gt; "📋 Verification Logs" and can be exported as
              CSV.</>
            : <>Bir backtest sonucunun yanındaki <strong>"✓ OKX Verisiyle Doğrula"</strong> butonu, o
              run'daki HER işlemin giriş/çıkış fiyatının, işlemin gerçekleştiği mumun gerçek [düşük,
              yüksek] aralığına düşüp düşmediğini otomatik kontrol eder — %100 deterministik, ücretsiz,
              yapay zeka içermez. Sonuçlar Backtest &gt; Geçmiş &gt; "📋 Doğrulama Logları"nda toplanır ve
              CSV olarak indirilebilir.</>}
        </p>
      </Section>

      <Section title={isEn ? '📈 Trade Visualization' : '📈 İşlem Görselleştirmesi'}>
        <p className="sh-text">
          {isEn
            ? 'The 📈 button next to any backtest trade opens it with the real OKX candle data from the moment it happened, the indicators used, and a replay of the entry/exit moment — see the "History & Reports" section for details.'
            : 'Herhangi bir backtest işleminin yanındaki 📈 butonu, o işlemi gerçekleştiği andaki gerçek OKX mum verisiyle, kullanılan göstergelerle ve giriş/çıkış anını tekrar oynatarak açar — detayları için "Geçmiş & Raporlar" bölümüne bakın.'}
        </p>
      </Section>
    </>
  );
}

// ── 8. İşlem Geçmişi, Raporlar, İşlem Görselleştirmesi ────────────────
function HistoryTab({ lang }) {
  const isEn = lang === 'en';
  return (
    <>
      <Section title={isEn ? '📜 Trade History' : '📜 İşlem Geçmişi'}>
        <p className="sh-text">
          {isEn
            ? 'At the bottom of the main screen, the full list of your live (bot/manual) and test trades. You can apply filters (pair, side, status open/closed), select rows to bulk-delete, and export to CSV.'
            : 'Ana ekranın alt kısmında, hem canlı (bot/manuel) hem test işlemlerinizin tam listesi yer alır. Parite, yön, durum (açık/kapalı) gibi filtreler uygulayabilir, satırları seçip toplu silebilir, ve CSV olarak dışa aktarabilirsiniz.'}
        </p>
      </Section>

      <Section title={isEn ? '📊 Reports' : '📊 Raporlar'}>
        <p className="sh-text">
          {isEn
            ? <>The "📊 Report" button opens a two-tab analysis screen: <strong>Trade History</strong>
              (a full sortable, filterable table) and <strong>Analysis Report</strong> — cumulative
              profit/loss chart, win rate, Profit Factor, average win/loss, best/worst trade, per-pair
              breakdown, and average holding time, among other summary stats.</>
            : <>"📊 Rapor" butonu iki sekmeli bir analiz ekranı açar: <strong>İşlem Geçmişi</strong>
              (sıralanabilir, filtrelenebilir tam tablo) ve <strong>Analiz Raporu</strong> — kümülatif
              kâr/zarar grafiği, kazanma oranı, Profit Factor, ortalama kazanç/kayıp, en iyi/en kötü
              işlem, parite bazlı dağılım ve ortalama pozisyon süresi gibi özet istatistikler.</>}
        </p>
      </Section>

      <Section title={isEn ? '📈 Trade Visualization — the "Description" Section' : "📈 İşlem Görselleştirmesi — 'Açıklama' Bölümü"}>
        <p className="sh-text">
          {isEn
            ? 'The 📈 button next to any trade (live or backtest) opens it with the REAL OKX candle data from the moment it happened. On the chart:'
            : 'Herhangi bir işlemin (canlı veya backtest) yanındaki 📈 butonu, o işlemi gerçekleştiği andaki GERÇEK OKX mum verisiyle açar. Grafikte:'}
        </p>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--muted)', lineHeight: 1.8 }}>
          <li>{isEn ? 'Entry/Exit/SL/TP prices are marked with horizontal lines.' : 'Giriş/Çıkış/SL/TP fiyatları yatay çizgilerle işaretlenir.'}</li>
          <li>{isEn
            ? 'The ▶ Play button replays how the trade unfolded, candle by candle, close to real time (speed adjustable) — so you can see step by step whether the decision really made sense at that moment.'
            : '▶ Oynat butonuyla işlemin oluşumu mum mum, gerçek zamana yakın canlandırılır (hız ayarlanabilir), böylece kararın o an gerçekten mantıklı olup olmadığını adım adım görebilirsiniz.'}</li>
          <li>{isEn
            ? 'Hovering updates O/H/L/C, the indicators, AND candle statistics (O/C %, O/C value, High/Low %, High/Low value — with a + sign if the candle is green, - if red) live.'
            : 'Fare ile gezinince O/H/L/C, göstergeler VE mum istatistikleri (A/K %, A/K Değer, Maks/Min %, Maks/Min Değer — mum yeşilse +, kırmızıysa - işaretiyle) anlık günceller.'}</li>
        </ul>
        <p className="sh-text">
          {isEn
            ? <>At the bottom, under the <strong>"Description"</strong> heading, there's an automatically
              generated text explaining the trade's whole life cycle: what parameters it ran with
              (Context), why it opened at that moment (Entry), what happened while it stayed open — e.g.
              did the trailing stop kick in (Position Process), and exactly why/how it closed (Exit).</>
            : <>En altta <strong>"Açıklama"</strong> başlığı altında, o işlemin tüm hayat döngüsünü anlatan
              otomatik üretilmiş bir metin bulunur: hangi parametrelerle çalıştırıldığı (Bağlam), neden
              o an açıldığı (Giriş Anı), açık kaldığı süre boyunca ne olduğu — ör. trailing stop devreye
              girdi mi (Pozisyon Süreci), ve tam olarak neden/nasıl kapandığı (Çıkış).</>}
        </p>
        <Note>
          {isEn
            ? 'This description is NOT "written" by an AI — it\'s generated instantly from the trade\'s real, known data (prices, time, indicator values, closing reason code) with plain code logic; no data is ever sent to any external service.'
            : 'Bu açıklama yapay zeka ile "yazılmaz" — işlemin gerçek, bilinen verilerinden (fiyatlar, zaman, gösterge değerleri, kapanış sebebi kodu) düz kod mantığıyla anında üretilir; hiçbir harici servise veri gönderilmez.'}
        </Note>
      </Section>
    </>
  );
}

// ── 9. Doğrulama & Teknik (sadece admin) ──────────────────────────────
// Not: Ekran Görüntüsü (OCR/Claude Vision) doğrulama bölümü buradan kaldırıldı — backend
// endpoint'leri (verify-screenshot) hâlâ kodda duruyor ama hiçbir UI elemanı onları çağırmıyor,
// yani kullanıcı bu özelliğe uygulama içinden asla erişemiyor. Aktif/erişilebilir tek sistem
// OKX Veri-Bazlı Doğrulama'dır.
function TechnicalTab({ lang }) {
  const isEn = lang === 'en';
  return (
    <>
      <Section title={isEn ? '✓ OKX Data-Based Verification (deterministic)' : '✓ OKX Veri-Bazlı Doğrulama (deterministik)'}>
        <p className="sh-text">
          {isEn
            ? 'This is the only way to check the accuracy of backtest and live trades. Backtest is already fed from OKX\'s historical candle data. This verification checks whether every recorded trade\'s entry/exit price falls within the real [low, high] candle range — 100% exact, deterministic, contains no AI/visual reading, and is free.'
            : 'Backtest ve canlı işlemlerin doğruluğunu kontrol etmenin tek yolu budur. Backtest zaten OKX\'in geçmiş mum verisinden beslenir. Bu doğrulama, kaydedilen her işlemin giriş/çıkış fiyatının gerçek [low, high] mum aralığına düşüp düşmediğini kontrol eder — %100 kesin, deterministik, hiçbir AI/görsel okuma içermez ve ücretsizdir.'}
        </p>
        <p className="sh-text">
          {isEn
            ? <>Where: Backtest &gt; Single Test/Parameter Sweep/History — <code> ✓ Verify with OKX
              Data</code> as soon as a result appears. Logs: Backtest &gt; History &gt;
              <code> 📋 Verification Logs</code> (CSV downloadable).</>
            : <>Nerede: Backtest &gt; Tekli Test/Parametre Taraması/Geçmiş — sonuç göründüğü an
              <code> ✓ OKX Verisiyle Doğrula</code>. Loglar: Backtest &gt; Geçmiş &gt;
              <code> 📋 Doğrulama Logları</code> (CSV indirilebilir).</>}
        </p>
        <pre style={{ fontSize: 11, background: 'var(--surface2)', padding: '8px 10px', borderRadius: 6, overflowX: 'auto' }}>
{`POST /api/backtest/verify-data          { runId }
GET  /api/backtest/verify-data-logs     ?runId=... (${isEn ? 'optional' : 'opsiyonel'})`}
        </pre>
        <p className="sh-text">
          {isEn ? 'Source: ' : 'Kaynak: '}<code>backend/src/services/backtestVerify.js</code>, <code>backend/src/routes/backtest.js</code>.
        </p>
      </Section>

      <Section title={isEn ? '⚠ Known Limitations' : '⚠ Bilinen Sınırlamalar'}>
        <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, color: 'var(--muted)', lineHeight: 1.8 }}>
          <li>{isEn
            ? 'This verification only checks whether the price falls within the candle\'s range; it does not separately verify the logical correctness of the strategy\'s own decision.'
            : 'Bu doğrulama sadece fiyatın mum aralığına düşüp düşmediğini kontrol eder; stratejinin kararının kendisinin mantıksal doğruluğunu ayrıca doğrulamaz.'}</li>
        </ul>
      </Section>
    </>
  );
}

export default function HelpModal({ onClose, role }) {
  const { lang } = useTranslation();
  const isEn = lang === 'en';
  const TABS = [
    { id: 'start',    label: isEn ? 'Getting Started' : 'Başlarken' },
    { id: 'dash',     label: isEn ? 'Dashboard' : 'Ana Ekran' },
    { id: 'manual',   label: isEn ? 'Manual Trading' : 'Manuel İşlem' },
    { id: 'bot',      label: isEn ? 'Bot Trading' : 'Bot İşlemleri' },
    { id: 'strategy', label: isEn ? 'Strategies' : 'Stratejiler' },
    { id: 'settings', label: isEn ? 'Settings' : 'Ayarlar' },
    { id: 'backtest', label: 'Backtest' },
    { id: 'history',  label: isEn ? 'History & Reports' : 'Geçmiş & Raporlar' },
    { id: 'tech',     label: isEn ? 'Verification (Technical)' : 'Doğrulama (Teknik)', adminOnly: true },
  ];
  const [tab, setTab] = useState('start');
  const { offset: dragOffset, onMouseDown: handleHeaderMouseDown } = useDraggable();
  const visibleTabs = TABS.filter(tb => !tb.adminOnly || role === 'admin');
  useTrackPageView(`Help - ${visibleTabs.find(tb => tb.id === tab)?.label || tab}`);

  return (
    <div className="log-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="sh-modal" style={{ width: 860, transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)` }}>

        <div className="sh-header" onMouseDown={handleHeaderMouseDown}>
          <div>
            <h2 className="sh-title">{isEn ? 'TrendSniper Help' : 'TrendSniper Yardım'}</h2>
            <p className="sh-subtitle">{isEn ? 'From signing up to opening trades, from bots to backtesting — the whole app' : "Kayıttan işlem açmaya, botlardan backtest'e — uygulamanın tamamı"}</p>
          </div>
          <button className="sh-close" onClick={onClose}>✕</button>
        </div>

        <div className="settings-tabs" style={{ padding: '0 24px', flexShrink: 0, flexWrap: 'wrap' }}>
          {visibleTabs.map(tb => (
            <button
              key={tb.id}
              className={`settings-tab ${tab === tb.id ? 'active' : ''}`}
              onClick={() => setTab(tb.id)}
            >
              {tb.label}
            </button>
          ))}
        </div>

        <div className="sh-body">
          {tab === 'start'    && <GettingStartedTab lang={lang} />}
          {tab === 'dash'     && <DashboardTourTab lang={lang} />}
          {tab === 'manual'   && <ManualTradingTab lang={lang} />}
          {tab === 'bot'      && <BotTab lang={lang} />}
          {tab === 'strategy' && <StrategiesTab lang={lang} />}
          {tab === 'settings' && <SettingsTab lang={lang} />}
          {tab === 'backtest' && <BacktestTab lang={lang} />}
          {tab === 'history'  && <HistoryTab lang={lang} />}
          {tab === 'tech' && role === 'admin' && <TechnicalTab lang={lang} />}
        </div>

        <div className="sh-footer">
          <button className="btn btn-start" onClick={onClose}>{isEn ? 'Got it, close' : 'Anladım, kapat'}</button>
        </div>

      </div>
    </div>
  );
}
